
import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import AuthMixin from "@/common/mixins/auth.mixin";
import DatePicker from "vue2-datepicker";
import { getDateFromIsoDate, getDatesFromIsoDate, getWeekDayEnum } from "@/utils/date.util";
import "vue2-datepicker/index.css";
import ValidatedFormMixin from "@/common/mixins/validated-form.mixin";
import ShiftMixin from "../shift.mixin";
import { PayRateStatisticsDto, ShiftDataDto, ShiftDateDto, ShiftGroupFormDataDto, ShiftGroupFormDto } from "@/types/shift.type";
import { OfficeDto } from "@/types/office.type";
import { Position } from "@/common/constants";

@Component({
  components: {
    DatePicker
  },
  computed: {
    ...mapState("Shift", {
      selectedOffice: "selectedOffice",
      offices: "offices",
      formData: "shiftFormData",
      form: "shiftForm",
      shiftData: "shiftData",
      statistics: "payRateStatistics",
      loading: "loading"
    }),
  },
})
export default class PostShift extends Mixins(AuthMixin, ValidatedFormMixin, ShiftMixin) {
  @Prop() isKanban!: boolean;
  @Prop() isEdit!: boolean;

  selectedDates: Date[] = [];
  form!: ShiftGroupFormDto;
  formData!: ShiftGroupFormDataDto;
  statistics!: PayRateStatisticsDto | null;
  selectedOffice!: OfficeDto;
  offices!: OfficeDto[];
  shiftData!: ShiftDataDto | null;
  loading!: boolean;

  get postToOptions(): { text: string, value: number, disabled: boolean }[] {
    var isDisabled = this.formData.favoriteCount === 0;
    return [
      { text: 'All', value: 0, disabled: isDisabled }, 
      { text: 'Favorites Only', value: 1, disabled: isDisabled },
    ];
  }

  get otherFormGroupDisabled(): boolean {
    if (!this.shiftData) {
      return false;
    }

    return this.form.positionId != Position.DentalHygienist.getId(this.shiftData.positions);
  }

  get postTo(): number[] {
    return [this.form.postTo];
  }

  get timeIn(): string | null | undefined {
    return this.form.dates[0].timeIn
  }

  set timeIn(timeIn: string | null | undefined) {
    if (timeIn) {
      this.form.dates[0].timeIn = timeIn;
    }
  }

  get timeOut(): string | null | undefined {
    return this.form.dates[0].timeOut
  }

  set timeOut(timeOut: string | null | undefined) {
    if (timeOut) {
      this.form.dates[0].timeOut = timeOut;
    }
  }

  get isSingleDate(): boolean {
    return (this.form.dates?.length || 0) === 1;
  }

  set postTo(value: number[]) {
    if (value.length === 0) {
      this.form.postTo = this.form.postTo === 0 ? 1 : 0;
      return;
    }

    var newValue = value[value.length - 1];
    if (newValue != this.form.postTo)
      this.form.postTo = newValue;
  }

  get payRate(): string | undefined {
    return this.form.payRate?.toFixed(2);
  }

  get positionName(): string | undefined {
    return this.formData.positions.find(option => option.value === this.form.positionId)?.text;
  }

  to12Hour(time: string): string {
    let hour = parseInt(time.slice(0, 2), 10);
    const minutes = time.slice(3, 5);
    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;

    return `${hour}:${minutes} ${period}`;
  }
  
  onDateInput(dates: Date[]): void {
    if (dates.length === 0 && this.form.id) return;

    const toDto = (d: Date): ShiftDateDto => {
      const hrs = this.formData.hours.find(h => h.dayOfWeek === getWeekDayEnum(d));
      return {
        date: d.toLocaleDateString('sv'),
        timeIn: hrs?.openTime ?? '',
        timeOut: hrs?.closeTime ?? ''
      };
    };

    this.form.dates = dates.map(toDto);
    this.selectedDates = dates;

    if (this.form.dates.length === 1) {
      this.timeIn  = this.form.dates[0].timeIn;
      this.timeOut = this.form.dates[0].timeOut;
    } else {
      this.timeIn  = null;
      this.timeOut = null;
    }
  }

  disabledDate(date: Date): boolean {
    if (this.form.id) {
      const d0 = getDateFromIsoDate(this.form.dates[0].date);
      d0.setHours(0,0,0,0);
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return d.getTime() !== d0.getTime();
    }

    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(); today.setHours(0,0,0,0);

    const unsetHours = this.formData.hours.filter(h =>
      (h.openTime === '00:00:00' && h.closeTime === '00:00:00') ||
      (!h.openTime && !h.closeTime)
    );
    if (unsetHours.some(h => getWeekDayEnum(d) === h.dayOfWeek)) return true;

    return d < today;
  }

  disabledCalendarChanger(date: Date): boolean {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();

    const today = new Date(); today.setHours(0,0,0,0);

    if (m === 0 && d === 1) {
      const endOfYear = new Date(y, 11, 31);
      return endOfYear < today;
    }

    if (d === 1) {
      const endOfMonth = new Date(y, m + 1, 0);
      return endOfMonth < today;
    }

    const endOfTargetMonth = new Date(y, m + 1, 0);
    return endOfTargetMonth < today;
  }

  formatDate(dateStr: string): string {
    let d: Date;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (m) {
      const [_, y, mo, da] = m;
      d = new Date(Number(y), Number(mo) - 1, Number(da));
    } else {
      d = new Date(dateStr);
    }

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const formatted = d.toLocaleDateString('en-US', options);

    const day = d.getDate();
    const suffix =
      day % 10 === 1 && day % 100 !== 11 ? 'st' :
      day % 10 === 2 && day % 100 !== 12 ? 'nd' :
      day % 10 === 3 && day % 100 !== 13 ? 'rd' : 'th';

    return formatted.replace(/\b(\d{1,2})\b/, m => m + suffix);
  }

  async onOfficeSelect(value: OfficeDto): Promise<void> {
    this.$store.commit("Shift/updateField", {
      path: "selectedOffice",
      value: value
    });

    this.setLoading(true);

    await this.fetchData();

    this.setLoading(false);
  }

  setPayRate(value: string | undefined): void {
    if (value) 
      this.form.payRate = parseFloat(value);
    else {
      this.form.payRate = 0;
    }
  }

  async mounted(): Promise<void> {
    this.setLoading(true);
    await this.fetchData();
    if (!this.form.id) {
      this.$store.commit("Shift/updateField", {
        path: "payRateStatistics",
        value: null
      })
    }
    this.setLoading(false);
  }

  async fetchData() : Promise<void> {
    await this.$store.dispatch("Shift/getShiftFormData", this.selectedOffice.id);
  }

  onShow(): void {
    this.selectedDates = getDatesFromIsoDate(this.form.dates.map(d => d.date));
  }

  async createShift(): Promise<void> {
    this.setLoading(true);

    this.$validator.validateAll().then(async (isValid: boolean) => {
      if (isValid) {
        this.form.officeId = this.selectedOffice.id;

        this.form.breakTime = 0;

        if (this.form.channel){
          this.form.channel = parseInt(this.form.channel.toString());
        }

        await this.$store.dispatch("Shift/saveShift", this.form);

        this.$emit('success');
        this.$bvModal.hide("create-edit-shift-modal");
      }

      this.setLoading(false);
    });
  }

  async onPositionSelect(value: number): Promise<void> {
    if (!this.shiftData) {
      return;
    }

    const officeRates = this.formData.rates.find(rate => rate.positionId === value);

    this.form.payRate = officeRates?.amount ?? null;

    if (value != Position.DentalHygienist.getId(this.shiftData.positions)) {
      this.form.channel = null;
    }

    this.setLoading(true);
    await this.$store.dispatch("Shift/getPayRateStatistics", value);
    this.setLoading(false);
  }

  closeModal(): void {
    this.$bvModal.hide("create-edit-shift-modal");
  }
}
